import {
  Component,
  DOCUMENT,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  SdsPublicListContext,
  SdsPublicListError,
  SdsPublicListRow,
  SdsPublicPictogram
} from '../models/sds-public.model';
import { SdsPublicApiService } from '../services/sds-public-api.service';
import { resolveSdsPictogramUiOption } from '../services/sds-api.service';
import { LanguageId } from 'src/app/_models/language-id.enum';

/** One page of rows. Kept small: this is a phone screen held at arm's length on a work site. */
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * How close to the end of the list a scroll has to come before the next page is fetched.
 * Roughly one card's worth of runway, so the rows are usually there before the guest arrives.
 */
const INFINITE_SCROLL_MARGIN_PX = 320;

/**
 * Pictograms drawn on a collapsed card. The rest collapse into a "+N" badge that opens the
 * detail sheet, where every symbol is listed with its name. A chemical carrying six GHS
 * symbols would otherwise wrap the card to three rows on a phone.
 */
const MAX_VISIBLE_PICTOGRAMS = 4;

/**
 * Norwegian emergency numbers, shown in the sticky footer.
 * Not configurable: this is a Norwegian HSE product, and the point of the footer is that the
 * numbers are the same everywhere and always reachable without thinking.
 */
export const POISON_INFO_PHONE = '+4722591300';
export const POISON_INFO_DISPLAY = '22 59 13 00';
export const EMERGENCY_PHONE = '113';

type SortKey = 'ProductName' | 'Manufacturer';
type PublicLanguage = 'en' | 'no';

/** A chip standing for one active filter, with the action that clears it. */
interface ActiveFilterChip {
  label: string;
  clear: () => void;
}

/** A choice in the filter sheet. */
interface FilterChoice {
  label: string;
  value: string;
  /** Local SVG for a pictogram choice; empty for the others. */
  svg?: string;
}

/**
 * The read-only chemical list a guest reaches by scanning a business' QR code.
 *
 * Read-only is enforced by the API, not by this page: there is no write endpoint behind the
 * public token at all. What this component owns is the other half of the requirement — being
 * usable one-handed on a phone, next to the chemical, by someone who does not work here.
 *
 * The layout follows the SDS scanner design: a fixed header and emergency footer with a single
 * scrolling column between them, filters behind a bottom sheet rather than stacked down the
 * page, and one unmissable button per card for the thing people actually came for.
 *
 * The list loads a page at a time and appends: scrolling towards the end fetches the next one,
 * so a long inventory is read by scrolling rather than by finding a pager. Nothing is ever
 * fetched that the guest has not scrolled towards, and a page that fails to load leaves the
 * rows already on screen alone and offers a retry.
 */
@Component({
  selector: 'sds-public-list',
  templateUrl: 'sds-public-list.component.html',
  styleUrls: ['sds-public-list.component.css'],
  standalone: false
})
export class SdsPublicListComponent implements OnInit, OnDestroy {
  token = '';

  context: SdsPublicListContext | null = null;
  rows: SdsPublicListRow[] = [];

  loading = true;
  rowsLoading = false;
  /** Set when the link itself is unusable — revoked, expired or unknown. */
  loadError: SdsPublicListError | null = null;

  searchTerm = '';
  pictogramFilter = '';
  manufacturerFilter = '';
  riskFilter = '';
  sortColumn: SortKey = 'ProductName';
  sortDirection: 'ASC' | 'DESC' = 'ASC';

  pageNumber = 1;
  readonly pageSize = PAGE_SIZE;
  totalCount = 0;
  totalPages = 0;

  /** Set while a further page is being appended, as opposed to the list being replaced. */
  appendingPage = false;

  /**
   * Why appending the last page failed, or null. Auto-loading stops until the guest retries,
   * and the reason picks the message they are given.
   */
  appendError: SdsPublicListError | null = null;

  /** Seconds the API's Retry-After header asked for, when it sent one. */
  retryAfterSeconds: number | null = null;

  /** The filter/sort bottom sheet. */
  filterSheetOpen = false;

  /** The chemical whose detail sheet is open, or null. */
  detailsRow: SdsPublicListRow | null = null;

  /**
   * A guest has no Kuba profile, so there is no stored language preference to read. The page
   * offers the same two languages as the login screen and remembers the choice the same way.
   */
  currentLang: PublicLanguage = 'no';

  /** Choices in the filter sheet, rebuilt when the context or the language changes. */
  pictogramChoices: FilterChoice[] = [];
  manufacturerChoices: FilterChoice[] = [];
  riskChoices: FilterChoice[] = [];

  readonly maxVisiblePictograms = MAX_VISIBLE_PICTOGRAMS;
  readonly poisonInfoPhone = POISON_INFO_PHONE;
  readonly poisonInfoDisplay = POISON_INFO_DISPLAY;
  readonly emergencyPhone = EMERGENCY_PHONE;

  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private fillHandle: ReturnType<typeof setTimeout> | null = null;
  private scrollElement: HTMLElement | null = null;
  /** Identifies the newest request, so a late response cannot overwrite a newer list. */
  private latestRequestId = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private publicApi: SdsPublicApiService,
    private zone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * The scrolling column. It sits inside the *ngIf that waits for the context, so it does not
   * exist when the view is first created — hence a setter rather than ngAfterViewInit.
   */
  @ViewChild('scrollArea')
  set scrollArea(reference: ElementRef<HTMLElement> | undefined) {
    this.detachScrollListener();
    this.scrollElement = reference?.nativeElement ?? null;

    if (!this.scrollElement) {
      return;
    }

    // Outside Angular on purpose: scroll fires every frame, and running change detection over
    // a list of cards that often is what makes a phone list feel heavy. The handler re-enters
    // the zone only on the frame that actually starts a fetch.
    this.zone.runOutsideAngular(() => {
      this.scrollElement?.addEventListener('scroll', this.handleScroll, { passive: true });
    });
  }

  ngOnInit(): void {
    this.restoreLanguage();

    // translate.use() resolves asynchronously, so the filter labels are rebuilt when the new
    // language has actually loaded. Rebuilding straight after the call would read the raw keys
    // back out of instant(). Row labels are methods in the template and re-render on their own.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.buildFilterChoices());

    this.token = (this.route.snapshot.paramMap.get('token') || '').trim();

    if (!this.token) {
      this.loading = false;
      this.loadError = 'unavailable';
      return;
    }

    this.loadContext();
  }

  ngOnDestroy(): void {
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }

    if (this.fillHandle) {
      clearTimeout(this.fillHandle);
    }

    this.detachScrollListener();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Language =====

  /** Mirrors the login screen: languageId 1 is English, anything else Norwegian. */
  private restoreLanguage(): void {
    const storedLanguageId = localStorage.getItem('languageId');
    this.setLanguage(Number(storedLanguageId) === LanguageId.English ? 'en' : 'no');
  }

  setLanguage(lang: PublicLanguage): void {
    this.currentLang = lang;
    this.translate.use(lang);
    localStorage.setItem('languageId', lang === 'en' ? '1' : '2');
    localStorage.setItem('languageMode', lang === 'en' ? 'en-GB' : 'no-NO');
    this.document.documentElement.lang = lang;
  }

  // ===== Loading =====

  private loadContext(): void {
    this.loading = true;

    this.publicApi
      .getPublicContext(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: context => {
          this.context = context;
          this.buildFilterChoices();
          this.loading = false;
          this.fetchPage();
        },
        error: error => {
          this.loading = false;
          this.context = null;
          this.loadError = this.describeFailure(error);
        }
      });
  }

  private buildFilterChoices(): void {
    const options = this.context?.FilterOptions;
    if (!options) {
      return;
    }

    // Several stored rows can resolve to the same GHS symbol, which would otherwise show up as
    // two identical-looking buttons. The first row to claim a symbol keeps it.
    const seenSymbols = new Set<string>();

    this.pictogramChoices = options.Pictograms
      .map(pictogram => ({
        label: this.pictogramLabel(pictogram),
        value: pictogram.GHS,
        svg: this.pictogramSvg(pictogram)
      }))
      .filter(choice => {
        const symbol = choice.svg || choice.label;
        if (seenSymbols.has(symbol)) {
          return false;
        }

        seenSymbols.add(symbol);
        return true;
      });

    this.manufacturerChoices = options.Manufacturers.map(manufacturer => ({
      label: manufacturer,
      value: manufacturer
    }));

    this.riskChoices = options.RiskLevels.map(level => ({
      label: this.riskLabel(level),
      value: level
    }));
  }

  /**
   * Fetches the current page.
   *
   * `append` separates the two things that ask for one: a replacement — first load, a new
   * search term, a changed filter or sort — and the next page of the same query, which is
   * added below the rows already on screen.
   */
  private fetchPage(append = false): void {
    if (append) {
      this.appendingPage = true;
    } else {
      this.rowsLoading = true;
    }

    // Both are set again by the error handler if this attempt fails too, so a stale wait time
    // from an earlier 429 cannot outlive it.
    this.appendError = null;
    this.retryAfterSeconds = null;

    // A search typed while page three is still in flight would otherwise have its own page one
    // overwritten by the late response. Only the newest request may touch the list.
    const requestId = ++this.latestRequestId;

    this.publicApi
      .getPublicChemicals(this.token, {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        search: this.searchTerm.trim() || null,
        pictogram: this.pictogramFilter || null,
        manufacturer: this.manufacturerFilter || null,
        risk: this.riskFilter || null,
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: page => {
          if (requestId !== this.latestRequestId) {
            return;
          }

          const items = page.Items || [];

          this.rows = append ? this.rows.concat(items) : items;
          this.totalCount = page.TotalCount || 0;
          this.totalPages = page.TotalPages || 0;
          this.rowsLoading = false;
          this.appendingPage = false;

          // A new query starts at the top, or the guest lands somewhere in the middle of a
          // list they have not seen the beginning of.
          if (!append) {
            this.scrollToTop();
          }

          this.fillScrollArea();
        },
        error: error => {
          if (requestId !== this.latestRequestId) {
            return;
          }

          if (append) {
            // Keep what is already on screen, and step back so a retry asks for the same page.
            this.pageNumber--;
            this.appendingPage = false;
            this.appendError = this.describeFailure(error);
          } else {
            this.rowsLoading = false;
            this.rows = [];
            this.totalCount = 0;
            this.totalPages = 0;
          }

          if (this.isLinkDeadStatus(error?.status)) {
            // Revoking a code has to cut off a page that is already open, not just new scans.
            this.loadError = 'unavailable';
            this.context = null;
            this.closeAllSheets();
          } else if (!append && this.isThrottledStatus(error?.status)) {
            // Nothing is wrong with the link or the connection, so neither message would be
            // true. The guest is told to wait, which is the only thing that helps.
            this.loadError = 'throttled';
            this.closeAllSheets();
          }
        }
      });
  }

  /** A status that means the link no longer resolves, rather than a transient failure. */
  private isLinkDeadStatus(status: number | undefined): boolean {
    // 404 (unknown), 410 (revoked or expired) and 403 (business no longer active) all mean the
    // same thing to whoever is standing there with a phone: this link is finished.
    return status === 404 || status === 410 || status === 403;
  }

  /** The API's per-client request budget is spent. Temporary, and worth saying so. */
  private isThrottledStatus(status: number | undefined): boolean {
    return status === 429;
  }

  /**
   * Turns a failed request into the message the guest gets, and remembers how long the API
   * asked them to wait when it said so.
   */
  private describeFailure(error: { status?: number; headers?: HttpHeaders }): SdsPublicListError {
    if (this.isLinkDeadStatus(error?.status)) {
      return 'unavailable';
    }

    if (this.isThrottledStatus(error?.status)) {
      this.retryAfterSeconds = this.readRetryAfter(error);
      return 'throttled';
    }

    return 'network';
  }

  /**
   * Retry-After as whole seconds, or null.
   *
   * The API sends it in seconds and the CORS policy exposes it, but a proxy in between may send
   * the HTTP-date form or drop the header altogether, so nothing here is assumed.
   */
  private readRetryAfter(error: { headers?: HttpHeaders }): number | null {
    const header = error?.headers?.get('Retry-After');

    if (!header) {
      return null;
    }

    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.ceil(seconds);
    }

    const until = Date.parse(header);
    if (Number.isNaN(until)) {
      return null;
    }

    return Math.max(1, Math.ceil((until - Date.now()) / 1000));
  }

  retry(): void {
    this.loadError = null;
    this.retryAfterSeconds = null;
    this.loadContext();
  }

  // ===== Search =====

  onSearch(value: string): void {
    this.searchTerm = value ?? '';

    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }

    this.searchDebounceHandle = setTimeout(() => {
      this.pageNumber = 1;
      this.fetchPage();
    }, SEARCH_DEBOUNCE_MS);
  }

  // ===== Filter sheet =====

  openFilterSheet(): void {
    this.filterSheetOpen = true;
  }

  closeFilterSheet(): void {
    this.filterSheetOpen = false;
  }

  /**
   * Filter choices toggle rather than accumulate: the API takes one value per dimension, so
   * tapping the selected one clears it.
   */
  togglePictogram(value: string): void {
    this.pictogramFilter = this.pictogramFilter === value ? '' : value;
    this.reload();
  }

  toggleRisk(value: string): void {
    this.riskFilter = this.riskFilter === value ? '' : value;
    this.reload();
  }

  toggleManufacturer(value: string): void {
    this.manufacturerFilter = this.manufacturerFilter === value ? '' : value;
    this.reload();
  }

  pickSort(column: SortKey, direction: 'ASC' | 'DESC'): void {
    this.sortColumn = column;
    this.sortDirection = direction;
    this.reload();
  }

  isSortActive(column: SortKey, direction: 'ASC' | 'DESC'): boolean {
    return this.sortColumn === column && this.sortDirection === direction;
  }

  private reload(): void {
    this.pageNumber = 1;
    this.fetchPage();
  }

  /** Number shown on the filter button's badge. */
  get activeFilterCount(): number {
    return [this.pictogramFilter, this.riskFilter, this.manufacturerFilter]
      .filter(value => !!value).length;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  /** One chip per active filter, so what is narrowing the list stays visible above the rows. */
  get activeFilterChips(): ActiveFilterChip[] {
    const chips: ActiveFilterChip[] = [];

    if (this.pictogramFilter) {
      chips.push({
        label: this.choiceLabel(this.pictogramChoices, this.pictogramFilter),
        clear: () => this.togglePictogram(this.pictogramFilter)
      });
    }

    if (this.riskFilter) {
      chips.push({
        label: this.choiceLabel(this.riskChoices, this.riskFilter),
        clear: () => this.toggleRisk(this.riskFilter)
      });
    }

    if (this.manufacturerFilter) {
      chips.push({
        label: this.manufacturerFilter,
        clear: () => this.toggleManufacturer(this.manufacturerFilter)
      });
    }

    return chips;
  }

  private choiceLabel(choices: FilterChoice[], value: string): string {
    return choices.find(choice => choice.value === value)?.label ?? value;
  }

  clearFilters(): void {
    this.pictogramFilter = '';
    this.manufacturerFilter = '';
    this.riskFilter = '';
    this.reload();
  }

  /** Clears the search as well; offered from the empty state. */
  clearEverything(): void {
    // Drop any pending debounce, or it fires a second identical fetch a moment later.
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    this.searchTerm = '';
    this.clearFilters();
  }

  // ===== Detail sheet =====

  openDetails(row: SdsPublicListRow): void {
    this.detailsRow = row;
  }

  closeDetails(): void {
    this.detailsRow = null;
  }

  private closeAllSheets(): void {
    this.filterSheetOpen = false;
    this.detailsRow = null;
  }

  // ===== Progressive loading =====

  /** Whether the query has pages the guest has not been given yet. */
  get hasMorePages(): boolean {
    return this.pageNumber < this.totalPages;
  }

  /**
   * Appends the next page. Also the retry behind the button: a failed append left `pageNumber`
   * on the last page that did arrive, so this asks for the same one again.
   */
  loadMore(): void {
    if (!this.hasMorePages || this.rowsLoading || this.appendingPage) {
      return;
    }

    this.pageNumber++;
    this.fetchPage(true);
  }

  /**
   * Bound once so the same reference can be removed again, and deliberately cheap: it runs on
   * every scroll frame, outside Angular, and only re-enters the zone when a page is due.
   */
  private readonly handleScroll = (): void => {
    if (!this.canLoadMore || !this.isNearBottom()) {
      return;
    }

    this.zone.run(() => this.loadMore());
  };

  private detachScrollListener(): void {
    this.scrollElement?.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Scrolling loads the next page on its own, but only until something goes wrong: after a
   * failure the guest taps to retry, rather than the scroll handler hammering a failing
   * endpoint once per frame.
   */
  private get canLoadMore(): boolean {
    return this.hasMorePages && !this.rowsLoading && !this.appendingPage && !this.appendError;
  }

  private isNearBottom(): boolean {
    const element = this.scrollElement;

    if (!element) {
      return false;
    }

    return element.scrollHeight - element.scrollTop - element.clientHeight
      <= INFINITE_SCROLL_MARGIN_PX;
  }

  /**
   * A page that does not fill the column leaves nothing to scroll, and scrolling is what asks
   * for the next page. On a tall screen this walks the list forward until the column can
   * actually be scrolled, so a tablet does not dead-end after twenty rows.
   */
  private fillScrollArea(): void {
    if (this.fillHandle) {
      clearTimeout(this.fillHandle);
      this.fillHandle = null;
    }

    if (!this.canLoadMore) {
      return;
    }

    // After the rows have been rendered, not before: their height is the whole question.
    this.fillHandle = setTimeout(() => {
      this.fillHandle = null;

      const element = this.scrollElement;

      if (!element || !this.canLoadMore) {
        return;
      }

      if (element.scrollHeight - element.clientHeight <= INFINITE_SCROLL_MARGIN_PX) {
        this.loadMore();
      }
    });
  }

  private scrollToTop(): void {
    if (this.scrollElement) {
      this.scrollElement.scrollTop = 0;
    }
  }

  // ===== Row rendering =====

  /** The pictograms that fit on a collapsed card. */
  visiblePictograms(row: SdsPublicListRow): SdsPublicPictogram[] {
    return row.Pictograms.slice(0, MAX_VISIBLE_PICTOGRAMS);
  }

  /** How many pictograms the "+N" badge stands for. Zero means no badge. */
  hiddenPictogramCount(row: SdsPublicListRow): number {
    return Math.max(0, row.Pictograms.length - MAX_VISIBLE_PICTOGRAMS);
  }

  /** Names of the pictograms behind the "+N" badge, for its tooltip and aria-label. */
  hiddenPictogramNames(row: SdsPublicListRow): string {
    return row.Pictograms
      .slice(MAX_VISIBLE_PICTOGRAMS)
      .map(pictogram => this.pictogramLabel(pictogram))
      .join(', ');
  }

  /**
   * The local GHS symbol for a stored pictogram row.
   *
   * The stored fields are not what their names suggest — LookupKey is a blob URL and GHS is a
   * phrase like "Acute toxicity" — so the row is resolved through the shared alias table rather
   * than by key lookup. An unrecognised row returns no image and is simply not drawn.
   */
  pictogramSvg(pictogram: SdsPublicPictogram): string {
    return resolveSdsPictogramUiOption(pictogram)?.svg ?? '';
  }

  /**
   * Prefers the translated GHS name, falling back to whatever text the backend stored so an
   * unrecognised pictogram still reads as something rather than as a blank chip.
   */
  pictogramLabel(pictogram: SdsPublicPictogram): string {
    const ghsCode = resolveSdsPictogramUiOption(pictogram)?.ghs;

    if (ghsCode) {
      const key = `SDS_PICTOGRAM_${ghsCode.toUpperCase()}`;
      const translated = this.translate.instant(key);

      if (translated !== key) {
        return translated;
      }
    }

    return pictogram.Label || pictogram.GHS || '';
  }

  riskLabel(riskLevel: string): string {
    switch ((riskLevel || '').toLowerCase()) {
      case 'low':
        return this.translate.instant('SDS_RISK_LOW');
      case 'medium':
        return this.translate.instant('SDS_RISK_MEDIUM');
      case 'high':
        return this.translate.instant('SDS_RISK_HIGH');
      default:
        return '';
    }
  }

  /** Drives the risk pill's colour class. Unassessed chemicals get no pill at all. */
  riskClass(riskLevel: string): string {
    return `is-${(riskLevel || 'none').toLowerCase()}`;
  }

  /** The pill carries an icon as well as a colour, so the band is not colour-only. */
  riskIcon(riskLevel: string): string {
    switch ((riskLevel || '').toLowerCase()) {
      case 'high':
        return 'pi pi-exclamation-triangle';
      case 'medium':
        return 'pi pi-exclamation-circle';
      case 'low':
        return 'pi pi-check-circle';
      default:
        return 'pi pi-minus-circle';
    }
  }

  openSafetyDataSheet(row: SdsPublicListRow): void {
    if (!row.HasSafetyDataSheet) {
      return;
    }

    window.open(
      this.publicApi.getPublicChemicalFileUrl(this.token, row.Id),
      '_blank',
      'noopener'
    );
  }

  /** Stable identity so paging does not rebuild every card. */
  trackByRowId(_index: number, row: SdsPublicListRow): number {
    return row.Id;
  }
}
